// LegendInsightsInsights.jsx
import React, { useState, useMemo } from "react";

const STAKEHOLDERS = [
  {
    id: "lawyers",
    label: "Lawyers / Advocates",
    emoji: "⚖️",
    pains: [
      "High administrative burden and repetitive drafting work.",
      "Case tracking across multiple courts is fragmented.",
      "Client follow-ups and updates consume valuable time.",
      "Difficulty accessing structured legal research quickly.",
      "Irregular cashflows due to unpredictable case timelines.",
    ],
    opportunities: [
      "AI-assisted drafting, research & case-prep automation.",
      "Smart case dashboards with deadlines, alerts & updates.",
      "Digital client communication + e-invoicing.",
      "Skill upgrades through Continuous Legal Education (CLE).",
    ],
  },

  {
    id: "lawFirms",
    label: "Law Firms",
    emoji: "🏢",
    pains: [
      "Low productivity due to manual workflows.",
      "Difficulty ensuring quality/consistency across associates.",
      "Billing leakage & poor time-tracking practices.",
      "Client retention challenges.",
      "Knowledge management scattered across teams.",
    ],
    opportunities: [
      "Firm-wide digital workflows & SOPs.",
      "Centralised knowledge repository + brief libraries.",
      "Automated timesheets & billing systems.",
      "Performance dashboards for teams & cases.",
    ],
  },

  {
    id: "clients",
    label: "Clients / Citizens",
    emoji: "🧑‍🤝‍🧑",
    pains: [
      "Lack of transparency on case progress & fees.",
      "Difficulty choosing the right lawyer.",
      "Delays, adjournments, and no clarity on timelines.",
      "Legal jargon & complexity.",
      "Scattered documents and missed deadlines.",
    ],
    opportunities: [
      "Transparent case-tracking interface.",
      "Verified lawyer profiles & ratings.",
      "Digital documents + reminders + secure storage.",
      "Structured legal pathways for common issues.",
    ],
  },

  {
    id: "judiciary",
    label: "Courts & Judiciary",
    emoji: "🏛️",
    pains: [
      "High case pendency & backlog.",
      "Manual filing errors and incomplete submissions.",
      "Limited real-time visibility into case analytics.",
      "Fragmented information across benches.",
      "Low digital adoption in lower courts.",
    ],
    opportunities: [
      "Standardised filings & automated checks.",
      "Digital case analytics for pendency & patterns.",
      "Court workflow optimisation.",
      "Better coordination with lawyers & departments.",
    ],
  },

  {
    id: "lawStudents",
    label: "Law Students & Interns",
    emoji: "🎓",
    pains: [
      "Limited access to practical case exposure.",
      "Unstructured internships.",
      "Difficulty building portfolios & getting mentors.",
      "Lack of affordable upskilling materials.",
      "Uncertainty about specialisation paths.",
    ],
    opportunities: [
      "Practical skill modules + CLE pathways.",
      "AI-powered legal research training.",
      "Portfolio builders & internship matchmaking.",
      "Mentor-mentee community model.",
    ],
  },

  {
    id: "complianceTeams",
    label: "Compliance Officers / Corporates",
    emoji: "🧾",
    pains: [
      "Complex regulatory environment with frequent changes.",
      "Manual compliance tracking.",
      "Audit trails scattered across departments.",
      "High cost of compliance failures.",
      "Lack of real-time visibility across branches.",
    ],
    opportunities: [
      "Automated compliance calendars & alerts.",
      "Digital audit trails + standardised documentation.",
      "Central dashboards for enterprise-wide visibility.",
    ],
  },

  {
    id: "regulators",
    label: "Government & Regulators",
    emoji: "🏛️",
    pains: [
      "Low real-time compliance insights.",
      "Difficulty monitoring disputes & enforcement.",
      "Fragmented citizen data.",
      "Slow feedback loops from the legal ecosystem.",
      "Policy decisions without ground-level visibility.",
    ],
    opportunities: [
      "Aggregated dashboards for policy & governance.",
      "Digital compliance feeds from businesses.",
      "Standardised formats for filings & certifications.",
    ],
  },

  {
    id: "legalTech",
    label: "LegalTech & Solution Providers",
    emoji: "💻",
    pains: [
      "Fragmented APIs across courts & departments.",
      "Low adoption by traditional practitioners.",
      "Data reliability & lack of verification.",
      "Trust-building challenges in legal sector.",
      "Difficulty integrating with government systems.",
    ],
    opportunities: [
      "Unified interoperability rail across courts & firms.",
      "Verified digital identities & filings.",
      "Plug-and-play AI modules for firms & lawyers.",
      "B2G integration channels.",
    ],
  },

  {
    id: "enforcement",
    label: "Police & Enforcement Agencies",
    emoji: "👮‍♂️",
    pains: [
      "Document verification overload.",
      "Case coordination delays with courts & lawyers.",
      "Low digital case-chain visibility.",
      "Manual evidence handling with risk of errors.",
    ],
    opportunities: [
      "Real-time case linkage dashboard.",
      "Digitised summons/notices.",
      "Secure evidence chain with verifiable logs.",
    ],
  }
];

const USPs = [
  {
    id: "cleDashboard",
    title: "CLE & Case Insights Dashboard",
    desc: "Track your Continuing Legal Education credits, case summaries, and latest legal updates in one personalised dashboard.",
    emoji: "📚",
    group: "CLE",
  },
  {
    id: "legalDraftingAI",
    title: "AI Legal Drafting & Research",
    desc: "Draft contracts, petitions, and agreements faster with AI suggestions, legal research support, and precedent recommendations.",
    emoji: "🤖",
    group: "LEGEND Productivity",
  },
  {
    id: "lawyerSkillStack",
    title: "Lawyer Skill & Wellbeing Stack",
    desc: "Access microlearning, CLE modules, peer mentoring, and mental health support for sustainable legal practice.",
    emoji: "🎓💚",
    group: "Self & Community",
  },
  {
    id: "legalMarketplace",
    title: "Verified Legal Marketplace",
    desc: "Connect with law firms, clients, compliance experts, and freelance legal professionals through a secure, verified platform.",
    emoji: "🛒💼",
    group: "Market & Engagement",
  },
  {
    id: "communityEngagement",
    title: "Community & Knowledge Exchange",
    desc: "Participate in forums, discussion boards, case study sharing, and expert-led webinars for collaborative learning and growth.",
    emoji: "👥",
    group: "Self & Community",
  },
  {
    id: "digitalCard",
    title: "Digital Legal Card",
    desc: "Maintain a verified digital identity for lawyers and firms, integrating credentials, CLE history, and client ratings.",
    emoji: "📘",
    group: "Self & Community",
  },
  {
    id: "legendTube",
    title: "Law & Legal Podcasts / Videos",
    desc: "Access bite-sized legal shorts, podcasts, and expert interviews on landmark cases, regulations, and law practice tips.",
    emoji: "🎙️",
    group: "CLE",
  },
  {
    id: "caseTracker",
    title: "Case Tracking & Analytics",
    desc: "Monitor case progress, court schedules, and pending filings with automated reminders and AI analytics.",
    emoji: "📊",
    group: "LEGEND Productivity",
  },
];

/* -------------------- Helper UI subcomponents -------------------- */

const Pill = ({ children }) => (
  <span className="inline-block bg-cyan-700/40 text-cyan-100 px-2 py-0.5 rounded-full text-xs font-medium">
    {children}
  </span>
);

const ExpandIcon = ({ open }) => (
  <span className={`ml-2 text-sm ${open ? "rotate-180" : "rotate-0"} transition-transform`}>
    ▼
  </span>
);

/* -------------------- Main Component -------------------- */

export default function LegendInsights() {
  const [tab, setTab] = useState("stakeholders"); 
  const [query, setQuery] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const stakeholdersFiltered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return STAKEHOLDERS;
    return STAKEHOLDERS.filter(
      (s) =>
        s.label.toLowerCase().includes(q) ||
        s.pains.some((p) => p.toLowerCase().includes(q)) ||
        s.opportunities.some((o) => o.toLowerCase().includes(q))
    );
  }, [query]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-4 md:p-8 text-gray-100">
      {/* Header */}
      <header className="max-w-6xl mx-auto flex items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold tracking-tight">Think5Whys — LEGEND Insights</h1>
          <p className="text-sm text-gray-300 mt-1">Pain points, opportunities & USPs tailored for Hotels, Restaurants & Cafés.</p>
        </div>

        <div className="flex items-center gap-3">
          <Pill>India: 60K hotels • 5L organised • 2Cr unorganised</Pill>
          <Pill>MMR: 5K hotels • 1.5L outlets</Pill>
        </div>
      </header>

      {/* Tabs */}
      <nav className="max-w-6xl mx-auto mb-4 flex gap-2">
        <button
          onClick={() => setTab("stakeholders")}
          className={`px-3 py-2 rounded-md font-semibold text-sm ${
            tab === "stakeholders" ? "bg-cyan-600 text-black shadow" : "bg-gray-800 text-gray-200 hover:bg-gray-700"
          }`}
        >
          Stakeholders
        </button>
        <button
          onClick={() => setTab("usps")}
          className={`px-3 py-2 rounded-md font-semibold text-sm ${
            tab === "usps" ? "bg-cyan-600 text-black shadow" : "bg-gray-800 text-gray-200 hover:bg-gray-700"
          }`}
        >
          Think5Whys USPs
        </button>

        <div className="ml-auto flex items-center gap-2">
          <input
            aria-label="Search stakeholders"
            placeholder="Search stakeholders, pain or opportunity..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="bg-gray-800 text-gray-200 placeholder-gray-400 px-3 py-2 rounded-md text-sm w-56 md:w-80 focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />
          <button
            onClick={() => { setQuery(""); setExpandedId(null); }}
            className="text-sm bg-gray-700 px-3 py-2 rounded-md hover:bg-gray-600"
          >
            Reset
          </button>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto space-y-6">
        {/* Stakeholders Tab */}
        {tab === "stakeholders" && (
          <>
            <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {stakeholdersFiltered.map((s) => {
                const isOpen = expandedId === s.id;
                return (
                  <article key={s.id} className="bg-gradient-to-br from-gray-800 to-gray-850/30 rounded-lg border border-gray-700 shadow-sm overflow-hidden">
                    <div className="p-4 flex items-start gap-3">
                      <div className="text-3xl">{s.emoji}</div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-3">
                          <h3 className="text-lg font-semibold">{s.label}</h3>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-300">Stakeholder</span>
                            <button
                              onClick={() => setExpandedId(isOpen ? null : s.id)}
                              aria-expanded={isOpen}
                              className="ml-2 bg-cyan-700/30 text-sm px-2 py-1 rounded"
                            >
                              {isOpen ? "Collapse" : "Expand"} <ExpandIcon open={isOpen} />
                            </button>
                          </div>
                        </div>

                        {/* Snapshot */}
                        <p className="text-sm text-gray-300 mt-2 line-clamp-3">
                          <strong>Pain points:</strong> {s.pains[0]} • {s.pains[1]} • {s.pains[2]}
                        </p>

                        {/* Expanded details */}
                        {isOpen && (
                          <div className="mt-3 space-y-3 text-sm">
                            <div>
                              <h4 className="font-medium">Top 5 Pain Points</h4>
                              <ol className="list-decimal list-inside mt-1 space-y-1 text-gray-200">
                                {s.pains.map((p, idx) => (
                                  <li key={idx}>{p}</li>
                                ))}
                              </ol>
                            </div>

                            <div>
                              <h4 className="font-medium">Opportunities Think5Whys Can Deliver</h4>
                              <ul className="list-disc list-inside mt-1 space-y-1 text-gray-200">
                                {s.opportunities.map((o, idx) => (
                                  <li key={idx}>{o}</li>
                                ))}
                              </ul>
                            </div>

                            <div className="flex gap-2 mt-2">
                              <button
                                onClick={() => alert(`${s.label} — prototype action: Open Diagnostics`)}
                                className="text-sm bg-cyan-600 text-black px-3 py-1 rounded font-semibold"
                              >
                                Open Diagnostics
                              </button>
                              <button
                                onClick={() => alert(`${s.label} — prototype action: Create Action Plan`)}
                                className="text-sm bg-gray-700 px-3 py-1 rounded hover:bg-gray-600"
                              >
                                Create Action Plan
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </section>

            {stakeholdersFiltered.length === 0 && (
              <div className="bg-gray-800 p-4 rounded text-center text-gray-300">No results for your search.</div>
            )}
          </>
        )}

        {/* USPs Tab */}
        {tab === "usps" && (
          <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {USPs.map((u) => (
              <div key={u.id} className="bg-gradient-to-br from-cyan-700/20 to-gray-800 rounded-lg p-4 border border-cyan-600/20">
                <div className="flex items-start gap-3">
                  <div className="text-2xl">{u.emoji}</div>
                  <div>
                    <h3 className="font-semibold text-lg">{u.title}</h3>
                    <p className="text-sm text-gray-200 mt-2">{u.desc}</p>
                  </div>
                </div>

                <div className="mt-4 flex gap-2">
                  <button
                    onClick={() => alert(`Prototype: Explore ${u.title}`)}
                    className="bg-cyan-600 text-black px-3 py-2 rounded font-semibold text-sm"
                  >
                    Explore
                  </button>
                  <button
                    onClick={() => alert(`Prototype: Demo ${u.title}`)}
                    className="bg-gray-800 px-3 py-2 rounded text-gray-200 hover:bg-gray-700 text-sm"
                  >
                    Demo
                  </button>
                </div>
              </div>
            ))}
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className="max-w-6xl mx-auto mt-8 text-center text-sm text-gray-400">
        Think5Whys • LEGEND Insights • India & MMR snapshot — built for quick prototyping.
      </footer>
    </div>
  );
}
